import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { reducer, buildInitialState } from "./reducer";
import { api, BASE } from "@/lib/api";
import { useAuth } from "@/store/AuthContext";
import { syncConnectionsWithCatalog } from "@/domain/connections";
import {
  getProjectCharacters,
  getProjectReferences,
  getGlobalCharacters,
  getGlobalReferences,
  getPendingReviewResources,
} from "@/domain/librarySelectors";
import { setLanguage, t } from "@/i18n";
import { toast } from "sonner";
// Simple UUID generator fallback (no external dependency)
const uuidv4 = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15));

export const StudioContext = createContext(null);

const SplashLoader = () => (
  <div className="flex h-screen items-center justify-center bg-[#0d0d0d]" data-testid="studio-loading">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#303030] border-t-[#f5b544]" />
  </div>
);

// Selecciona el mensaje de error según el código HTTP
const loadErrMsg = (err) => {
  if (err?.status === 401) return t.common.loadError401;
  if (err?.status >= 500) return t.common.loadErrorServer;
  return t.common.loadError;
};
const saveErrMsg = (err) => {
  if (err?.status === 401) return t.common.saveError401;
  if (err?.status >= 500) return t.common.saveErrorServer;
  return t.common.saveError;
};

export function StudioProvider({ children }) {
  const { user } = useAuth();
  const isGuest = user?.role === "guest";
  const [state, dispatch] = useReducer(reducer, null, () => buildInitialState(!isGuest));
  const [ready, setReady] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const first = useRef(true);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const saveTimer = useRef(null);
  // job_id → intervalId para polling activo
  const pollingRef = useRef({});

  const refreshCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    setCatalogError(null);
    try {
      const catalogData = await api.getCatalog();
      setCatalog(catalogData);
      if (catalogData) {
        dispatch({
          type: "CONNECTIONS_SYNC_CATALOG",
          payload: catalogData,
        });
      }
      return catalogData;
    } catch (err) {
      setCatalogError(err);
      throw err;
    } finally {
      setLoadingCatalog(false);
    }
  }, []);

  // Hidrata estado y catálogo en paralelo
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [remoteResult, catalogResult] = await Promise.allSettled([
          api.getState(),
          api.getCatalog(),
        ]);
        if (!active) return;
        const catalogData = catalogResult.status === "fulfilled" ? catalogResult.value : null;
        if (catalogResult.status === "rejected") {
          setCatalogError(catalogResult.reason);
        } else {
          setCatalogError(null);
        }
        if (catalogData) setCatalog(catalogData);

        const remote = remoteResult.status === "fulfilled" ? remoteResult.value : null;
        if (remote) {
          if (!remote.onboarding?.ownerName && user?.name) {
            remote.onboarding = { ...(remote.onboarding || {}), ownerName: user.name };
          }
          if (catalogData) {
            remote.connections = syncConnectionsWithCatalog(remote.connections || [], catalogData);
          }
          dispatch({ type: "HYDRATE", payload: remote });
        } else if (remoteResult.status === "rejected") {
          toast.error(loadErrMsg(remoteResult.reason));
        } else {
          const initial = buildInitialState(!isGuest);
          if (!initial.onboarding.ownerName && user?.name) {
            initial.onboarding = { ...initial.onboarding, ownerName: user.name };
          }
          if (catalogData) {
            initial.connections = syncConnectionsWithCatalog(initial.connections || [], catalogData);
          }
          const { lastError, ...persisted } = initial;
          await api.saveState(persisted);
          dispatch({ type: "HYDRATE", payload: persisted });
        }
      } catch (err) {
        toast.error(loadErrMsg(err));
      } finally {
        if (active) {
          setReady(true);
          setLoadingCatalog(false);
        }
      }

      // Sincronizar jobs del backend DESPUÉS de setReady(true) para que el
      // auto-save los persista en studio_state en el siguiente cambio de estado.
      // Esto recupera jobs de vídeo (y otros) que no llegaron a persistirse por
      // lanzamiento de excepción en submitGeneration o race condition en auto-save.
      if (active) {
        try {
          const { jobs: backendJobs = [] } = await api.listJobs().catch(() => ({ jobs: [] }));
          if (active && backendJobs.length > 0) {
            // React 18 batcha los dispatches en async → un solo re-render y un solo auto-save
            backendJobs.forEach((job) => {
              dispatch({ type: "JOB_UPSERT", payload: job });
              if (["submitted", "polling", "not_sent"].includes(job.submission_state)) {
                _startJobPolling(job.job_id);
              }
            });
          }
        } catch { /* non-fatal — estado queda con los jobs hidratados */ }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincrónico, durante el render
  setLanguage(state.onboarding.language);

  useEffect(() => {
    if (!ready) return;
    if (first.current) { first.current = false; return; }
    const { lastError, ...persisted } = state;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.saveState(persisted).catch((err) => toast.error(saveErrMsg(err)));
    }, 600);
    return () => clearTimeout(saveTimer.current);
  }, [state, ready]);

  // ── Generación ────────────────────────────────────────────────────────────

  /** Envía una generación y registra la tarea localmente.
   *  No reenvía si ya existe el mismo idempotency_key. */
  const submitGeneration = async (params) => {
    const idempotencyKey = params.idempotency_key || uuidv4();
    const payload = { ...params, idempotency_key: idempotencyKey };
    const job = await api.submitGeneration(payload);
    dispatch({ type: "JOB_UPSERT", payload: job });
    // Iniciar polling si está en curso
    if (["submitted", "polling", "not_sent"].includes(job.submission_state)) {
      _startJobPolling(job.job_id);
    }
    return job;
  };

  /** Envía un job de upscale y registra la tarea localmente. */
  const submitUpscale = async (params) => {
    const idempotencyKey = params.idempotency_key || uuidv4();
    const payload = { ...params, idempotency_key: idempotencyKey };
    const job = await api.submitUpscale(payload);
    dispatch({ type: "JOB_UPSERT", payload: job });
    if (["submitted", "polling", "not_sent"].includes(job.submission_state)) {
      // Upscale polling: getUpscaleJob o getJob (mismo collection)
      _startUpscalePolling(job.job_id);
    }
    return job;
  };

  const _startUpscalePolling = (jobId) => {
    if (pollingRef.current[jobId]) return;
    const interval = setInterval(async () => {
      try {
        const job = await api.getUpscaleJob(jobId);
        dispatch({ type: "JOB_UPSERT", payload: job });
        const terminal = ["completed", "failed", "cancelled", "uncertain", "blocked"];
        if (terminal.includes(job.submission_state)) {
          clearInterval(pollingRef.current[jobId]);
          delete pollingRef.current[jobId];
          if (job.submission_state === "completed" && job.asset_id && job.asset_url) {
            dispatch({
              type: "ASSET_ADD",
              payload: {
                id: job.asset_id,
                type: "image",
                url: `${BASE}${job.asset_url}`,
                source: "generation",
                label: job.asset_label || "upscale",
                projectId: job.project_id || null,
                sceneId: job.scene_id || null,
                parentAssetId: job.source_asset_id || null,
                isDemo: false,
              },
            });
          }
        }
      } catch (err) {
        console.warn("upscale poll error", err);
      }
    }, 5000);
    pollingRef.current[jobId] = interval;
  };

  const _startJobPolling = (jobId) => {
    if (pollingRef.current[jobId]) return; // ya está en polling
    const interval = setInterval(async () => {
      try {
        const job = await api.getJob(jobId);
        dispatch({ type: "JOB_UPSERT", payload: job });
        const terminal = ["completed", "failed", "cancelled", "uncertain", "blocked"];
        if (terminal.includes(job.submission_state)) {
          clearInterval(pollingRef.current[jobId]);
          delete pollingRef.current[jobId];
          if (job.submission_state === "completed") {
            // Añadir activo generado al estado local para que aparezca en galería
            if (job.asset_id && job.asset_url) {
              dispatch({
                type: "ASSET_ADD",
                payload: {
                  id: job.asset_id,
                  // upscale y edit producen imágenes aunque su kind sea distinto
                  type: ["edit", "upscale"].includes(job.kind) ? "image" : (job.kind || "image"),
                  url: `${BASE}${job.asset_url}`,
                  source: job.is_simulation ? "simulation" : "generation",
                  label: job.asset_label || "resultado",
                  projectId: job.project_id || null,
                  sceneId: job.scene_id || null,
                  nodeId: job.node_id || null,
                  parentAssetId: job.source_asset_id || null,
                  originalAssetId: job.source_asset_id || null,
                  isDemo: false,
                },
              });
              if (job.node_id) {
                const targetNode = (stateRef.current?.nodes || []).find((n) => n.id === job.node_id);
                const ndata = targetNode?.data || {};
                const parseTime = (ts) => (ts ? new Date(ts).getTime() : 0);
                const jobTime = parseTime(job.created_at);
                const manualTime = parseTime(ndata.assetUpdatedAt);
                const latestJobTime = parseTime(ndata.latestJobCreatedAt);

                const isManualNewer =
                  (ndata.assetSource === "manual" || ndata.assetSource === "unlinked") &&
                  manualTime > jobTime;
                const isJobOutdated = latestJobTime > jobTime;

                if (!isManualNewer && !isJobOutdated) {
                  dispatch({
                    type: "NODE_UPDATE",
                    payload: {
                      id: job.node_id,
                      data: {
                        assetId: job.asset_id,
                        assetSource: "generation",
                        assetUpdatedAt: new Date().toISOString(),
                        assetJobId: job.job_id,
                        manualAssetSelection: false,
                      },
                    },
                  });
                }
              }
            }
            toast.success(t.generation?.completedToast || "Generación completada");
          } else if (job.submission_state === "failed") {
            toast.error(t.generation?.failedToast || "La generación falló");
          }
        }
      } catch {
        // error transitorio; el polling continúa en la siguiente iteración
      }
    }, 5000);
    pollingRef.current[jobId] = interval;
  };

  /** Simula la finalización de una tarea (solo cuando no hay clave real configurada).
   *  Crea un activo placeholder y completa la tarea. */
  const simulateJob = async (jobId) => {
    const job = await api.simulateJob(jobId);
    dispatch({ type: "JOB_UPSERT", payload: job });
    if (job.submission_state === "completed" && job.asset_id && job.asset_url) {
      dispatch({
        type: "ASSET_ADD",
        payload: {
          id: job.asset_id,
          type: job.kind === "edit" ? "image" : (job.kind || "image"),
          url: `${BASE}${job.asset_url}`,
          source: "simulation",
          label: job.asset_label || "resultado-simulado",
          projectId: job.project_id || null,
          sceneId: job.scene_id || null,
          nodeId: job.node_id || null,
          parentAssetId: job.source_asset_id || null,
          originalAssetId: job.source_asset_id || null,
          isDemo: false,
        },
      });
      if (job.node_id) {
        const targetNode = (stateRef.current?.nodes || []).find((n) => n.id === job.node_id);
        const ndata = targetNode?.data || {};
        const parseTime = (ts) => (ts ? new Date(ts).getTime() : 0);
        const jobTime = parseTime(job.created_at);
        const manualTime = parseTime(ndata.assetUpdatedAt);
        const latestJobTime = parseTime(ndata.latestJobCreatedAt);

        const isManualNewer =
          (ndata.assetSource === "manual" || ndata.assetSource === "unlinked") &&
          manualTime > jobTime;
        const isJobOutdated = latestJobTime > jobTime;

        if (!isManualNewer && !isJobOutdated) {
          dispatch({
            type: "NODE_UPDATE",
            payload: {
              id: job.node_id,
              data: {
                assetId: job.asset_id,
                assetSource: "generation",
                assetUpdatedAt: new Date().toISOString(),
                assetJobId: job.job_id,
                manualAssetSelection: false,
              },
            },
          });
        }
      }
    }
    return job;
  };

  const value = useMemo(
    () => ({
      state,
      dispatch,
      catalog,
      catalogError,
      loadingCatalog,
      refreshCatalog,
      submitGeneration,
      submitUpscale,
      simulateJob,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, catalog, catalogError, loadingCatalog, refreshCatalog],
  );
  if (!ready) return <SplashLoader />;
  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}

export const useStudio = () => {
  const ctx = useContext(StudioContext);
  if (!ctx) throw new Error("useStudio fuera de StudioProvider");
  return ctx;
};

/** Devuelve los modelos del catálogo que soportan la capacidad indicada.
 *  kind = "image" | "video"
 *  Siempre desde el backend; nunca de una copia local. */
export const useCatalogModels = (kind) => {
  const { catalog } = useStudio();
  if (!catalog) return [];
  return (catalog.providers || []).flatMap((p) => {
    if (kind && !(p.capabilities || []).includes(kind)) return [];
    const models = p.models || [];
    const filtered = kind
      ? models.filter((m) => {
          const mKind = m.model_kind || m.modelKind || m.category || (p.capabilities?.length === 1 ? p.capabilities[0] : "image");
          return mKind === kind;
        })
      : models;
    return filtered.map((m) => ({
      ...m,
      providerLabel: p.label,
      keyConfigured: p.key_configured,
    }));
  });
};

export const useProjectData = (projectId) => {
  const { state } = useStudio();
  return useMemo(() => {
    const project = state.projects.find((p) => p.id === projectId) || null;
    const scenes = state.scenes.filter((s) => s.projectId === projectId).sort((a, b) => a.order - b.order);
    const assets = state.assets.filter((a) => a.projectId === projectId);
    const characters = getProjectCharacters(state, projectId);
    const references = getProjectReferences(state, projectId);
    const scripts = (state.scripts || []).filter((sc) => sc.projectId === projectId);
    const activeScript = scripts.find((sc) => sc.id === project?.activeScriptId || sc.isActive) || null;
    return {
      project,
      scenes,
      assets,
      characters,
      references,
      scripts,
      activeScript,
      nodes: state.nodes.filter((n) => n.projectId === projectId),
      edges: state.edges.filter((e) => e.projectId === projectId),
      thread: state.threads.find((th) => th.projectId === projectId) || { messages: [] },
      jobs: state.jobs.filter((j) => j.projectId === projectId),
      selection: state.selection[projectId] || { sceneId: null, assetId: null, nodeId: null, edgeId: null },
    };
  }, [state, projectId]);
};

export {
  getProjectCharacters,
  getProjectReferences,
  getGlobalCharacters,
  getGlobalReferences,
  getPendingReviewResources,
};

export const useLookups = () => {
  const { state } = useStudio();
  return useMemo(() => {
    const assetById = Object.fromEntries(state.assets.map((a) => [a.id, a]));
    const referenceById = Object.fromEntries(state.references.map((r) => [r.id, r]));
    const characterById = Object.fromEntries(state.characters.map((c) => [c.id, c]));
    const sceneById = Object.fromEntries(state.scenes.map((s) => [s.id, s]));
    const projectById = Object.fromEntries(state.projects.map((p) => [p.id, p]));
    return { assetById, referenceById, characterById, sceneById, projectById };
  }, [state.assets, state.references, state.characters, state.scenes, state.projects]);
};
